"use client";

import {
  getBalanceHealthPhysicianAssigneeForName,
  getBalanceHealthPhysicianAssigneeForNpi,
  getBalanceHealthRecallTypeForFaxNumber,
} from "@/configuration/groups/balance-health/configuration";
import { BH_FAX_LINE_TO_RECALL_TYPE, getBhFaxLineFromNpi } from "@/configuration/groups/balance-health/routing";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { ReferralSchedulingRecommendation } from "@/src/components/comms/referral-scheduling-recommendation";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { ReferralEligibilityCard } from "@/src/components/tasks/referral-eligibility-card";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { formatIsoDate, formatPhoneNumber, formatUSPhoneNumber } from "@/src/lib/utils/date-and-phone-utils";
import { Referral } from "@/src/models/documents/referral";
import { getFullAddress, getPatientName, getPatientPhoneNumber } from "@/src/models/patient";
import { getPersonName } from "@/src/models/person";

const BALANCE_HEALTH_WORKFLOW_TYPE = "Recall";
const BALANCE_HEALTH_DEFAULT_ASSIGNEE = "User, Assort Health";

interface BalanceHealthReferralInstructionsProps {
  referral: Referral;
  faxRecipientNumber: string | null;
  dueDate: string;
  staffLocation: string | null;
  taskId: string;
  // Whether the group verifies scheduling eligibility on a dedicated task instead of inline here.
  // When false (e.g. Balance Health production, which isn't on the task rollout yet) we keep the
  // inline recommendation/verification controls so those groups don't lose them entirely.
  schedulingVerificationTaskEnabled: boolean;
}

/**
 * Resolves the ModMed recall type for the referral instructions.
 * Priority: receiving provider's NPI → inbound fax line.
 * Falls back to the fax line when the provider NPI is absent or not mapped to a Balance Health
 * practice location (i.e. the provider is not a Balance Health provider).
 */
function getRecallType(referral: Referral, faxRecipientNumber: string | null): string | null {
  const npi = referral.receivingProvider?.npiNumber;
  const providerFaxLine = getBhFaxLineFromNpi(npi);

  if (providerFaxLine !== null) {
    return BH_FAX_LINE_TO_RECALL_TYPE[providerFaxLine];
  }

  if (!faxRecipientNumber) return null;

  try {
    const normalized = formatPhoneNumber(faxRecipientNumber);

    return getBalanceHealthRecallTypeForFaxNumber(normalized);
  } catch {
    return null;
  }
}

// Canonical NPI-keyed roster name (with credential suffix, e.g. ", DPM") wins over a name match,
// which wins over the raw extracted name — same precedence as the CloudCruise assignee resolver
// (`resolveReceivingProvider` in workflow-steps.ts), so the sync box matches what ModMed expects.
function getAssignTo(referral: Referral): string {
  const provider = referral.receivingProvider;

  if (!provider?.familyName) return BALANCE_HEALTH_DEFAULT_ASSIGNEE;

  return (
    getBalanceHealthPhysicianAssigneeForNpi(provider.npiNumber) ??
    getBalanceHealthPhysicianAssigneeForName(provider.familyName, provider.givenName) ??
    [provider.familyName, provider.givenName].filter(Boolean).join(", ")
  );
}

export function BalanceHealthReferralInstructions({
  referral,
  faxRecipientNumber,
  dueDate,
  staffLocation,
  taskId,
  schedulingVerificationTaskEnabled,
}: BalanceHealthReferralInstructionsProps) {
  const recallType = getRecallType(referral, faxRecipientNumber);
  const reason = referral.reasonForReferral ?? null;
  const assignTo = getAssignTo(referral);

  const referringProviderName = referral.referringProvider ? getPersonName(referral.referringProvider) : "";
  const referringProviderNpi = referral.referringProvider?.npiNumber ?? null;

  const patient = referral.patient;
  const patientName = patient ? getPatientName(patient) : "";
  const patientDob = patient?.dateOfBirth ? formatIsoDate(patient.dateOfBirth) : null;
  const patientPhoneRaw = patient ? getPatientPhoneNumber(patient) : null;
  const patientPhone = patientPhoneRaw ? formatUSPhoneNumber(patientPhoneRaw) : null;
  const patientAddress = patient ? getFullAddress(patient) : "";

  return (
    <div className="flex flex-col gap-8 w-full sm:min-w-[800px] max-w-[1200px] mx-auto">
      <EhrInstructionsCard>
        <div className="flex flex-col gap-4 text-sm">
          <span className="font-medium text-center">Balance Health Referral Instructions:</span>
          <p className="text-muted-foreground">
            Use the values below when creating the recall in ModMed. Click any value to copy.
          </p>

          <div className="space-y-3">
            <div>
              <span className="font-semibold text-foreground">Referring Provider</span>
              <div className="ml-4 mt-1 flex flex-col gap-1">
                <div>
                  <span className="text-muted-foreground">Name: </span>
                  {referringProviderName ? (
                    <CopyableContent text={referringProviderName} />
                  ) : (
                    <span className="text-muted-foreground italic">Not provided</span>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">NPI: </span>
                  {referringProviderNpi ? (
                    <CopyableContent text={referringProviderNpi} />
                  ) : (
                    <span className="text-muted-foreground italic">Not provided</span>
                  )}
                </div>
              </div>
            </div>

            <div>
              <span className="font-semibold text-foreground">Patient Demographics</span>
              <div className="ml-4 mt-1 flex flex-col gap-1">
                <div>
                  <span className="text-muted-foreground">Name: </span>
                  {patientName ? (
                    <CopyableContent text={patientName} />
                  ) : (
                    <span className="text-muted-foreground italic">Not provided</span>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">Date of Birth: </span>
                  {patientDob ? (
                    <CopyableContent text={patientDob} />
                  ) : (
                    <span className="text-muted-foreground italic">Not provided</span>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">Phone: </span>
                  {patientPhone ? (
                    <CopyableContent text={patientPhone} />
                  ) : (
                    <span className="text-muted-foreground italic">Not provided</span>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">Address: </span>
                  {patientAddress ? (
                    <CopyableContent text={patientAddress} />
                  ) : (
                    <span className="text-muted-foreground italic">Not provided</span>
                  )}
                </div>
              </div>
            </div>

            <div>
              <span className="text-muted-foreground">Workflow Type: </span>
              <CopyableContent text={BALANCE_HEALTH_WORKFLOW_TYPE} />
            </div>
            <div>
              <span className="text-muted-foreground">Recall Type: </span>
              {recallType ? (
                <CopyableContent text={recallType} />
              ) : (
                <span className="text-muted-foreground italic">Unknown recall type</span>
              )}
            </div>
            <div>
              <span className="text-muted-foreground">Due Date: </span>
              <CopyableContent text={dueDate} />
            </div>
            <div>
              <span className="text-muted-foreground">Note: </span>
              <CopyableContent text="Referral from PCP" />
            </div>
            <div>
              <span className="text-muted-foreground">Reason: </span>
              {reason ? (
                <CopyableContent text={reason} />
              ) : (
                <span className="text-muted-foreground italic">Not provided</span>
              )}
            </div>
            <div>
              <span className="text-muted-foreground">Assign To: </span>
              <CopyableContent text={assignTo} />
            </div>
            <div>
              <span className="text-muted-foreground">Staff Location: </span>
              {staffLocation ? (
                <CopyableContent text={staffLocation} />
              ) : (
                <span className="text-muted-foreground italic">Unknown</span>
              )}
            </div>
          </div>

          {/* Groups not yet on the dedicated Scheduling Eligibility Verification task keep verifying
              the recommendation inline here. */}
          {!schedulingVerificationTaskEnabled && (
            <ReferralSchedulingRecommendation referral={referral} title="Scheduling Recommendation" />
          )}
        </div>
      </EhrInstructionsCard>

      <ReferralEligibilityCard referralId={referral.id} taskId={taskId} />

      <PatientCard patient={referral.patient} />
      <ReferralProviderCards referral={referral} />
      <ReferralCard referral={referral} isEditable />
      {referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable />}
      {referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable />}
    </div>
  );
}
